import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { BeatLoader } from 'react-spinners'
import Sidebar from '../../layouts/Sidebar'
import Topbar from '../../layouts/Topbar'
import api from '../../config/api'

/* ─── helpers ─────────────────────────────────────────────────────────────── */

const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    const date = new Date(dateStr)
    return Number.isNaN(date.getTime())
        ? dateStr
        : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const formatAmount = (amount, currency) => {
    if (amount == null || amount === '') return '—'
    const num = Number(amount)
    const formatted = Number.isNaN(num)
        ? amount
        : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    if (currency === 'USD') return `$${formatted}`
    if (currency === 'ZIG') return `ZIG ${formatted}`
    return formatted
}

const getClientName = (clientName, clientDetails) => {
    if (clientName) return clientName
    if (!clientDetails) return '—'
    const name = [clientDetails.first_name, clientDetails.last_name].filter(Boolean).join(' ')
    return name || clientDetails.company_details?.name || clientDetails.email || '—'
}

const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase?.()) {
        case 'paid':
            return 'badge-soft-success'
        case 'draft':
            return 'badge-soft-warning'
        case 'sent':
            return 'badge-soft-primary'
        case 'cancelled':
            return 'badge-soft-danger'
        default:
            return 'badge-soft-secondary'
    }
}

const formatStatusLabel = (status) => {
    if (!status) return '—'
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

/* ─── component ───────────────────────────────────────────────────────────── */

const InvoiceDetails = () => {
    const { id } = useParams()
    const navigate = useNavigate()

    const [invoice, setInvoice] = useState(null)
    const [company, setCompany] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [statusUpdating, setStatusUpdating] = useState(false)
    const [recurringCreating, setRecurringCreating] = useState(false)

    const fetchData = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const [invoiceRes, companyRes] = await Promise.all([
                api.get(`/invoices/${id}/`),
                api.get('/auth/company/').catch(() => ({ data: null })),
            ])
            setInvoice(invoiceRes.data)
            setCompany(companyRes.data)
        } catch (err) {
            console.error('Failed to load invoice details', err)
            setError('Failed to load invoice details. Please try again.')
        } finally {
            setLoading(false)
        }
    }, [id])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const updateStatus = async (newStatus) => {
        if (!invoice) return
        setStatusUpdating(true)
        try {
            const res = await api.patch(`/invoices/${id}/`, { status: newStatus })
            setInvoice(res.data)
        } catch (err) {
            console.error('Failed to update status', err)
            alert('Failed to update invoice status.')
        } finally {
            setStatusUpdating(false)
        }
    }

    const createRecurringProfile = async () => {
        if (!invoice) return
        setRecurringCreating(true)
        try {
            await api.post(`/invoices/${id}/create-recurring/`)
            alert('Recurring profile created successfully!')
            navigate('/invoices/recurring')
        } catch (err) {
            console.error('Failed to create recurring profile', err)
            alert('Failed to create recurring profile.')
        } finally {
            setRecurringCreating(false)
        }
    }

    const printInvoice = () => {
        if (!invoice || !company) return

        const printWindow = window.open('', '_blank', 'width=800,height=900')
        if (!printWindow) {
            alert('Please allow popups to print')
            return
        }

        const client = invoice.client_details || {}
        const clientName = getClientName(invoice.client_name, invoice.client_details)
        const lineItems = invoice.line_items || []
        const logoUrl = company.logo 
            ? (company.logo.startsWith('http') ? company.logo : `http://127.0.0.1:8000${company.logo}`)
            : ''

        printWindow.document.write(`
            <html>
            <head>
                <title>Invoice ${invoice.invoice_number}</title>
                <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
                <style>
                    body { font-family: 'Inter', sans-serif; color: #333; padding: 40px; }
                    .invoice-header { border-bottom: 2px solid #eee; padding-bottom: 20px; margin-bottom: 30px; }
                    .company-logo { max-height: 70px; object-fit: contain; }
                    .invoice-title { font-size: 28px; font-weight: 700; color: #1e293b; }
                    .table th { background-color: #f8fafc; font-weight: 600; }
                    .total-box { background-color: #f8fafc; border-radius: 6px; padding: 15px; }
                    @media print {
                        body { padding: 0; }
                    }
                </style>
            </head>
            <body>
                <div class="container">
                    <div class="invoice-header d-flex justify-content-between align-items-center">
                        <div>
                            ${logoUrl ? `<img src="http:/${company.logo}" class="company-logo mb-2" alt="Company Logo" />` : ''}
                            <h4 class="fw-bold m-0">${company.name || 'Our Company'}</h4>
                            <p class="text-muted m-0 fs-13">${company.email || ''} | ${company.phone || ''}</p>
                            <p class="text-muted m-0 fs-13">${company.address || ''}</p>
                        </div>
                        <div class="text-end">
                            <span class="invoice-title">INVOICE</span>
                            <p class="m-0 fw-semibold text-primary">#${invoice.invoice_number}</p>
                            <p class="text-muted m-0 fs-13">Date: ${formatDate(invoice.invoice_date)}</p>
                            <p class="text-muted m-0 fs-13">Due Date: ${formatDate(invoice.due_date)}</p>
                        </div>
                    </div>

                    <div class="row mb-4">
                        <div class="col-6">
                            <h6 class="text-muted text-uppercase fs-12 fw-bold mb-2">Billed To</h6>
                            <h5 class="fw-bold m-0">${clientName}</h5>
                            <p class="text-muted m-0 fs-13">${client.email || ''}</p>
                            <p class="text-muted m-0 fs-13">${client.phone || ''}</p>
                            <p class="text-muted m-0 fs-13">${[client.address_line1, client.address_line2, client.city, client.country].filter(Boolean).join(', ')}</p>
                        </div>
                        <div class="col-6 text-end">
                            <h6 class="text-muted text-uppercase fs-12 fw-bold mb-2">Payment Details</h6>
                            <p class="m-0 fs-13"><strong>Currency:</strong> ${invoice.currency}</p>
                            <p class="m-0 fs-13"><strong>Status:</strong> ${invoice.status}</p>
                        </div>
                    </div>

                    <table class="table table-striped">
                        <thead>
                            <tr>
                                <th>Item / Description</th>
                                <th class="text-end">Quantity</th>
                                <th class="text-end">Unit Price</th>
                                <th class="text-end">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${lineItems.map(item => `
                                <tr>
                                    <td>
                                        <div class="fw-semibold">${item.name}</div>
                                        ${item.description ? `<small class="text-muted">${item.description}</small>` : ''}
                                    </td>
                                    <td class="text-end">${item.quantity}</td>
                                    <td class="text-end">${formatAmount(item.unit_price, invoice.currency)}</td>
                                    <td class="text-end">${formatAmount(item.line_total || (item.quantity * item.unit_price), invoice.currency)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>

                    <div class="row justify-content-end mt-4">
                        <div class="col-md-5">
                            <div class="total-box">
                                <div class="d-flex justify-content-between mb-2">
                                    <span class="text-muted">Sub Total</span>
                                    <span class="fw-semibold">${formatAmount(invoice.subtotal, invoice.currency)}</span>
                                </div>
                                <div class="d-flex justify-content-between border-top pt-2">
                                    <span class="fw-bold">Grand Total</span>
                                    <span class="fw-bold text-primary fs-18">${formatAmount(invoice.grand_total, invoice.currency)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    ${invoice.payment_instructions ? `
                        <div class="mt-4">
                            <h6 class="text-muted text-uppercase fs-12 fw-bold mb-1">Payment Instructions</h6>
                            <p class="fs-13 text-muted">${invoice.payment_instructions}</p>
                        </div>
                    ` : ''}

                    ${invoice.notes ? `
                        <div class="mt-2">
                            <h6 class="text-muted text-uppercase fs-12 fw-bold mb-1">Notes</h6>
                            <p class="fs-13 text-muted">${invoice.notes}</p>
                        </div>
                    ` : ''}
                </div>
                <script>
                    window.onload = function() {
                        window.print();
                    }
                </script>
            </body>
            </html>
        `)
        printWindow.document.close()
    }

    if (loading) {
        return (
            <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
                <Topbar />
                <Sidebar />
                <div className="page-wrapper d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
                    <BeatLoader color="#3550DC" size={15} />
                </div>
            </div>
        )
    }

    if (error || !invoice) {
        return (
            <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
                <Topbar />
                <Sidebar />
                <div className="page-wrapper">
                    <div className="content pb-0">
                        <div className="alert alert-danger m-3" role="alert">
                            <i className="ti ti-alert-circle me-2" />
                            {error || 'Invoice not found.'}
                        </div>
                        <Link to="/invoices" className="btn btn-light ms-3">
                            <i className="ti ti-arrow-left me-1" /> Back to Invoices
                        </Link>
                    </div>
                </div>
            </div>
        )
    }

    const client = invoice.client_details || {}
    const clientName = getClientName(invoice.client_name, invoice.client_details)

    return (
        <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content pb-0">

                    {/* Page Header */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">Invoice Details</h4>
                            <p className="text-muted mb-0">Invoice #{invoice.invoice_number}</p>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Link to="/invoices" className="btn btn-light btn-sm">
                                <i className="ti ti-arrow-left me-1" />
                                Back
                            </Link>
                            <Link to={`/edit-invoice/${invoice.id}`} className="btn btn-outline-secondary btn-sm">
                                <i className="ti ti-edit me-1" />
                                Edit Invoice
                            </Link>
                            <button type="button" onClick={printInvoice} className="btn btn-outline-danger btn-sm">
                                <i className="ti ti-file-type-pdf me-1" />
                                Print / PDF
                            </button>
                            <button
                                type="button"
                                onClick={createRecurringProfile}
                                disabled={recurringCreating}
                                className="btn btn-outline-info btn-sm"
                            >
                                <i className="ti ti-repeat me-1" />
                                {recurringCreating ? 'Creating…' : 'Make Recurring'}
                            </button>
                        </div>
                    </div>

                    <div className="row g-4">
                        {/* Main Invoice Card */}
                        <div className="col-lg-9">
                            <div className="card shadow-sm mb-4">
                                <div className="card-body p-4">
                                    
                                    {/* Invoice Header details */}
                                    <div className="d-flex justify-content-between align-items-start border-bottom pb-4 mb-4 flex-wrap gap-3">
                                        <div>
                                            {company && company.logo ? (
                                                <img src={`https:/` + company.logo} className="img-fluid mb-2" style={{ maxHeight: '60px' }} alt="Company Logo" />
                                            ) : null}
                                            <h5 className="fw-bold text-dark m-0">{company?.name || 'Burray HR Consultants'}</h5>
                                            <p className="text-muted fs-13 mb-0">{company?.email || 'info@burrayhr.com'}</p>
                                            <p className="text-muted fs-13 mb-0">{company?.phone || '+263242702772'}</p>
                                            <p className="text-muted fs-13 mb-0">{company?.address}</p>
                                        </div>
                                        <div className="text-end">
                                            <h3 className="fw-bold text-primary mb-1">INVOICE</h3>
                                            <span className="fs-14 fw-semibold text-dark">#{invoice.invoice_number}</span>
                                            <div className="mt-2 text-muted fs-13">
                                                <div><strong>Date:</strong> {formatDate(invoice.invoice_date)}</div>
                                                <div><strong>Due Date:</strong> {formatDate(invoice.due_date)}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Billed To & Info */}
                                    <div className="row mb-4">
                                        <div className="col-md-6">
                                            <h6 className="text-uppercase text-muted fs-12 fw-bold mb-2">Billed To</h6>
                                            <h5 className="fw-bold text-dark mb-1">{clientName}</h5>
                                            <p className="text-muted fs-13 mb-1">{client.email || '—'}</p>
                                            <p className="text-muted fs-13 mb-1">{client.phone || '—'}</p>
                                            <p className="text-muted fs-13 mb-0">
                                                {[client.address_line1, client.address_line2, client.city, client.country].filter(Boolean).join(', ')}
                                            </p>
                                        </div>
                                        <div className="col-md-6 text-md-end mt-3 mt-md-0">
                                            <h6 className="text-uppercase text-muted fs-12 fw-bold mb-2">Details</h6>
                                            <div className="fs-13">
                                                <div className="mb-1"><strong>Currency:</strong> {invoice.currency}</div>
                                                <div>
                                                    <strong>Status:</strong>{' '}
                                                    <span className={`badge ${getStatusBadgeClass(invoice.status)} ms-1`}>
                                                        {formatStatusLabel(invoice.status)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Line Items Table */}
                                    <div className="table-responsive mb-4">
                                        <table className="table table-hover border">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Item Name / Description</th>
                                                    <th className="text-end" style={{ width: '100px' }}>Quantity</th>
                                                    <th className="text-end" style={{ width: '150px' }}>Unit Price</th>
                                                    <th className="text-end" style={{ width: '150px' }}>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(invoice.line_items || []).map((item) => (
                                                    <tr key={item.id}>
                                                        <td>
                                                            <div className="fw-semibold text-dark">{item.name}</div>
                                                            {item.description && <small className="text-muted">{item.description}</small>}
                                                        </td>
                                                        <td className="text-end">{item.quantity}</td>
                                                        <td className="text-end">{formatAmount(item.unit_price, invoice.currency)}</td>
                                                        <td className="text-end fw-semibold text-dark">
                                                            {formatAmount(item.line_total || (Number(item.quantity) * Number(item.unit_price)), invoice.currency)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Total Box */}
                                    <div className="row justify-content-end mb-4">
                                        <div className="col-md-5 col-sm-7">
                                            <div className="bg-light p-3 rounded border">
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">Sub Total</span>
                                                    <span className="fw-semibold text-dark">{formatAmount(invoice.subtotal, invoice.currency)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between border-top pt-2">
                                                    <span className="fw-bold text-dark">Grand Total</span>
                                                    <span className="fw-bold text-primary fs-16">{formatAmount(invoice.grand_total, invoice.currency)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Additional info */}
                                    <div className="row g-3 border-top pt-4">
                                        {invoice.payment_instructions && (
                                            <div className="col-md-6">
                                                <h6 className="text-uppercase text-muted fs-12 fw-bold mb-2">Payment Instructions</h6>
                                                <p className="text-muted fs-13 mb-0" style={{ whiteSpace: 'pre-line' }}>
                                                    {invoice.payment_instructions}
                                                </p>
                                            </div>
                                        )}
                                        {invoice.notes && (
                                            <div className="col-md-6">
                                                <h6 className="text-uppercase text-muted fs-12 fw-bold mb-2">Notes</h6>
                                                <p className="text-muted fs-13 mb-0" style={{ whiteSpace: 'pre-line' }}>
                                                    {invoice.notes}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* Sidebar Actions Column */}
                        <div className="col-lg-3">
                            <div className="card shadow-sm mb-4">
                                <div className="card-header">
                                    <h6 className="fw-bold mb-0">Invoice Status Action</h6>
                                </div>
                                <div className="card-body">
                                    <p className="text-muted fs-13 mb-3">Update the status of this invoice directly:</p>
                                    <div className="d-grid gap-2">
                                        <button
                                            type="button"
                                            onClick={() => updateStatus('paid')}
                                            disabled={statusUpdating || invoice.status === 'paid'}
                                            className="btn btn-success btn-sm text-white"
                                        >
                                            Mark as Paid
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => updateStatus('sent')}
                                            disabled={statusUpdating || invoice.status === 'sent'}
                                            className="btn btn-primary btn-sm text-white"
                                        >
                                            Mark as Sent
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => updateStatus('draft')}
                                            disabled={statusUpdating || invoice.status === 'draft'}
                                            className="btn btn-warning btn-sm text-white"
                                        >
                                            Reset to Draft
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => updateStatus('cancelled')}
                                            disabled={statusUpdating || invoice.status === 'cancelled'}
                                            className="btn btn-danger btn-sm text-white"
                                        >
                                            Cancel Invoice
                                        </button>
                                    </div>
                                    {statusUpdating && (
                                        <div className="text-center mt-3">
                                            <BeatLoader color="#3550DC" size={8} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                    <p className="text-dark mb-0">
                        {new Date().getFullYear()} © <span className="link-primary">Burray Invoice</span>, All Rights Reserved
                    </p>
                </div>
            </div>
        </div>
    )
}

export default InvoiceDetails